import { PepDataContent } from './PepDataContent.tsx';
import { defaultPepInfo, PepSection } from '../constants.ts';
import { TClientPep, TPepEntity, TRelatedPep } from '../types.ts';
import { AddRemoveBlock } from '~/components/AddRemoveBlock/AddRemoveBlock.tsx';
import { ClientPepContent } from './ClientPepContent.tsx';
import { Box, Button, Grid, Typography } from '@mui/material';
import { styles } from '~/modules/identification/forms/AdditionalInformation/styles.ts';
import { Path, useFormContext } from 'react-hook-form';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal.tsx';

interface IPEPDataModal {
  section: PepSection | null;
  onClose: () => void;
  pepIndex: number;
}

export const PEPDataModal = ({ section, onClose, pepIndex }: IPEPDataModal) => {
  if (!section) {
    return null;
  }

  const { setValue, watch, trigger, getValues } = useFormContext<{
    additionalInformation: TPepEntity[];
  }>();

  const values = watch(`additionalInformation.${pepIndex}.${section}`);

  const setFieldValue = (
    field: keyof TPepEntity,
    value: TPepEntity[keyof TPepEntity],
    shouldValidate?: boolean
  ) => {
    setValue(
      `additionalInformation.${pepIndex}.${field}` as const,
      value,
      shouldValidate ? { shouldValidate: true } : undefined
    );
  };

  const addPepRecord = () => {
    setFieldValue(section, [...(values as TRelatedPep[]), defaultPepInfo]);
  };

  const removePepRecord = (recordIndex: number) => {
    setFieldValue(
      section,
      (values as TRelatedPep[]).filter((_, index) => index !== recordIndex)
    );
  };

  const touchAllSectionFields = () => {
    const sectionValues = getValues(`additionalInformation.${pepIndex}.${section}`);

    if (Array.isArray(sectionValues)) {
      sectionValues.forEach((_, i) => {
        setValue(
          `additionalInformation.${pepIndex}.${section}.${i}` as Path<{
            additionalInformation: TPepEntity[];
          }>,
          sectionValues[i],
          {
            shouldTouch: true,
          }
        );
      });
    } else if (sectionValues && typeof sectionValues === 'object') {
      (Object.keys(sectionValues) as (keyof TClientPep)[]).forEach((key) => {
        setValue(
          `additionalInformation.${pepIndex}.${section}.${key}` as Path<{
            additionalInformation: TPepEntity[];
          }>,
          sectionValues[key],
          { shouldTouch: true }
        );
      });
    }
  };

  const sectionConfigs = {
    [PepSection.familyPepsInfo]: {
      titlePep: '(член сім’ї)',
      sectionPlusText:
        'Натисніть на "+" для додавання інформації про наступного члена сім’ї РЕР (якщо така інформація наявна).',
      sectionMinusText: 'Натисніть на "-" для видалення інформації про члена сім’ї РЕР.',
      onSubmit: () => {
        setValue(`additionalInformation.${pepIndex}.isFamilyPep`, true, { shouldValidate: true });
        trigger(`additionalInformation.${pepIndex}.isFamilyPep`);
      },
    },
    [PepSection.pepRelatedPersonsInfo]: {
      titlePep: '(пов’язана особа)',
      sectionPlusText:
        'Натисніть на "+" для додавання інформації про наступну особу пов’язану з РЕР (якщо така інформація наявна).',
      sectionMinusText: 'Натисніть на "-" для видалення інформації про особу пов’язану з РЕР.',
      onSubmit: () => {
        setValue(`additionalInformation.${pepIndex}.isPepRelatedPerson`, true, {
          shouldValidate: true,
        });
        trigger(`additionalInformation.${pepIndex}.isPepRelatedPerson`);
      },
    },
    [PepSection.pepInfo]: {
      onSubmit: () => setFieldValue('isPep', true, true),
      titlePep: '',
      sectionPlusText: '',
      sectionMinusText: '',
    },
  };

  const onSubmit = () => {
    sectionConfigs[section].onSubmit();
    touchAllSectionFields();
    setTimeout(() => {
      Promise.all([
        trigger(`additionalInformation.${pepIndex}.pepInfo`),
        trigger(`additionalInformation.${pepIndex}.pepRelatedPersonsInfo`),
        trigger(`additionalInformation.${pepIndex}.familyPepsInfo`),
        trigger(`additionalInformation.${pepIndex}.isPep`),
        trigger(`additionalInformation.${pepIndex}.isPepRelatedPerson`),
        trigger(`additionalInformation.${pepIndex}.isFamilyPep`),
        trigger(`additionalInformation.${pepIndex}.isPepsAbsent`),
      ]).then(() => {
        trigger(`additionalInformation.${pepIndex}`);
      });
      onClose();
    }, 100);
  };

  return (
    <BaseModal open={!!section} onClose={onSubmit} containerProps={{ sx: styles.modalContainer }}>
      <Grid sx={styles.modalContent}>
        <Typography variant={'h4'} sx={styles.titleInfo}>
          Дані РЕР {sectionConfigs[section]?.titlePep}
        </Typography>
        {(values as TRelatedPep[])?.length ? (
          (values as TRelatedPep[]).map((_, index) => (
            <Grid key={index}>
              <Typography variant={'h5'}>{index + 1}. PEP</Typography>
              <Box sx={styles.wrapper}>
                <PepDataContent pepIndex={pepIndex} section={section} itemIndex={index} />
                <AddRemoveBlock
                  add={addPepRecord}
                  remove={() => removePepRecord(index)}
                  isPlusShown={
                    (values as TRelatedPep[]).length < 30 &&
                    index === (values as TRelatedPep[]).length - 1
                  }
                  isMinusShown={(values as TRelatedPep[]).length > 1}
                  plusText={sectionConfigs[section]?.sectionPlusText}
                  minusText={sectionConfigs[section]?.sectionMinusText}
                />
              </Box>
            </Grid>
          ))
        ) : (
          <Box sx={styles.wrapper}>
            <ClientPepContent section={section} pepIndex={pepIndex} />
          </Box>
        )}
        <Box sx={styles.buttonBox}>
          <Button onClick={onSubmit} variant={'contained'}>
            Продовжити
          </Button>
        </Box>
      </Grid>
    </BaseModal>
  );
};
